import JsSIP from 'jssip'
import type { RTCSession } from 'jssip/lib/RTCSession'
import { ReactNode, createContext, useEffect, useRef, useState } from 'react'

import { CallStatus } from '@/types'

const socket = new JsSIP.WebSocketInterface(process.env.REACT_APP_JS_SIP_SOCKET || '')
const ua = new JsSIP.UA({
  sockets: [socket],
  uri: process.env.REACT_APP_JS_SIP_URI || '',
  password: process.env.REACT_APP_JS_SIP_PASSWORD || '',
})
ua.start()

export const CallContext = createContext<{
  status: CallStatus
  isIncoming: boolean
  target: string
  modalIsVisible: boolean
  openModal: (phone: string) => void
  closeModal: () => void
  makeCall: () => void
  endCall: () => void
  answerCall: () => void
  holdCall: () => void
  unholdCall: () => void
    }>({
      status: '',
      isIncoming: false,
      target: '',
      modalIsVisible: false,
      openModal: () => {},
      closeModal: () => {},
      makeCall: () => {},
      endCall: () => {},
      answerCall: () => {},
      holdCall: () => {},
      unholdCall: () => {},
    })

export function CallProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<CallStatus>('')
  const [isIncoming, setIsIncoming] = useState(false)
  const [target, setTarget] = useState('')
  const [session, setSession] = useState<RTCSession | null>(null)
  const [modalIsVisible, setModalIsVisible] = useState(false)

  const localStream = useRef<MediaStream>()

  const localAudio = useRef<HTMLAudioElement | null>(null)
  const remoteAudio = useRef<HTMLAudioElement | null>(null)

  const openModal = (phone: string) => {
    if (modalIsVisible) return

    setTarget(phone)
    setModalIsVisible(true)
  }

  const makeCall = () => {
    console.log('makeCall session', session)
    if (session) {
      session.terminate()
      setSession(null)
    }

    setStatus('outgoing')
    setIsIncoming(false)

    if (!localStream.current) {
      getUserMedia()
    }

    const newSession = ua.call(target, {
      mediaStream: localStream.current,
      pcConfig: {
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      },
    })
    console.log('makeCall newSession', newSession)
    setSession(newSession)

    handleRemoteAudio(newSession)
    bindSessionEvents(newSession)
  }

  const endCall = () => {
    if (!session) return
    console.log('endCall session', session)

    // if (callStartTime !== null) {
    //   callDuration = Math.floor((Date.now() - callStartTime) / 1000)
    //   console.log('callDuration перед очищенням:', callDuration)
    //   document.getElementById('call-timer').innerText = `⏱ ${callDuration}s`
    // } else {
    //   console.log('❌ Call start time не знайдено')
    //   document.getElementById('call-timer').innerText = '⏱ 0s'
    // }

    // if (callTimerInterval) {
    //   clearInterval(callTimerInterval)
    //   callTimerInterval = null
    //   console.log('Таймер зупинено')
    // }

    session.terminate()
    setSession(null)
  }

  console.log('session', session)
  const answerCall = () => {
    if (!session || session.direction !== 'incoming') return

    if (!localStream.current) {
      getUserMedia()
    }

    session.answer({
      mediaStream: localStream.current,
      pcConfig: {
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      },
    })

    handleRemoteAudio(session)
  }

  const holdCall = () => {
    if (!session) return

    session.hold()
  }

  const unholdCall = () => {
    if (!session) return

    session.unhold()
  }

  const handleRemoteAudio = (session: RTCSession | null) => {
    if (!session) return

    const pc = session.connection

    pc.ontrack = (event) => {
      if (remoteAudio.current) {
        remoteAudio.current.srcObject = event.streams[0]
      }
    }
  }

  const bindSessionEvents = (session: RTCSession | null) => {
    if (!session) return

    session.on('confirmed', () => {
      console.log('✅ Зʼєднано')
      // callStartTime = Date.now()  // Встановлюємо час початку дзвінка
      // isCallActive = true
      // startCallTimer()
      setStatus('connected')
    })

    session.on('ended', () => {
      console.log('📴 Дзвінок завершено')
      setStatus('ended')
      endCall()
    })

    session.on('failed', (e) => {
      console.log('failed e', e)
      console.log('❌ Збій дзвінка')
      setStatus('failed')
      endCall()
    })

    session.on('hold', () => {
      console.log('⏸️ Дзвінок на паузі')
      setStatus('paused')
    })

    session.on('unhold', () => {
      console.log('▶️ Дзвінок відновлений')
      setStatus('unpaused')
    })
  }

  const getUserMedia = () => {
    navigator.mediaDevices
      .getUserMedia({ audio: true, video: false })
      .then((stream) => {
        localStream.current = stream
        if (localAudio.current) {
          localAudio.current.srcObject = stream
        }
      })
      .catch(console.error)
  }

  useEffect(() => {
    getUserMedia()

    ua.on('newRTCSession', (e: any) => {
      const incomingSession = e.session

      if (session) {
        incomingSession.terminate()
        return
      }

      setSession(incomingSession)

      if (incomingSession.direction === 'incoming') {
        setStatus(`incoming`)
        setIsIncoming(true)
        setTarget(incomingSession.remote_identity.uri.toString())
        setModalIsVisible(true)
      }

      bindSessionEvents(incomingSession)
    })
  }, [])

  return (
    <CallContext.Provider
      value={{
        status,
        isIncoming,
        target,
        modalIsVisible,
        openModal,
        closeModal: () => setModalIsVisible(false),
        makeCall,
        endCall,
        answerCall,
        holdCall,
        unholdCall,
      }}
    >
      {children}
      <audio
        ref={localAudio}
        autoPlay
        muted
        style={{ display: 'none' }}
      />
      <audio
        ref={remoteAudio}
        autoPlay
        style={{ display: 'none' }}
      />
    </CallContext.Provider>
  )
}
